/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define(['../../DynamicFileLoader'], function(DynamicFileLoader) {

	/**
	*  Live widget service that can be used to provide a live widget functionality
	*  outside of the dashboard environment
	*/
	class LiveWidgetService {

		constructor(options) {
			this._liveWidgetPreviewClass = options && options.liveWidgetPreviewClass;
		}

		initialize(glassContext) {
			this.glassContext = glassContext;
			this.logger = this.glassContext.getCoreSvc('.Logger');
			return Promise.resolve();
		}

		_getLiveWidgetPreviewClass() {
			return new Promise((resolve) => {
				if (this._liveWidgetPreviewClass) {
					return resolve(this._liveWidgetPreviewClass);
				} else {
					return DynamicFileLoader.load(['dashboard-analytics/preview/LiveWidgetPreview']).then((modules) => {
						resolve(modules[0]);
					});
				}
			});
		}

		_getEnvironment() {
			return this.glassContext.getSvc('.DashboardRuntime').then((svc) => {
				return svc.getRuntimeEnvironment();
			});
		}

		/**
		*	Transform a list of recommendations returned by the smarts recommender into an array of live widget specs
		*
		*	@param {object[]} - array of recommendations
		*	@param {object} - model information
		*
		*		{
		*			"assetId": "iD5075523AA154AD091DACC72F97823DC",
		*			"type": "uploadedFile"			// Optional - will save a CM query if available.
		*		}
		*
		*/
		transformRecommendationsToLiveWidgetSpecs(recommendations, modelInfo) {

			return this._getEnvironment().then((env) => {
				const dataSources = env.dashboardApi.getFeature('dataSources.deprecated');
				return this._getModule(dataSources, modelInfo).then(module => {
					const recommender = env.dashboardApi.getFeature('SmartsRecommender.deprecated');
					return recommender.transformRecommendationsToLiveWidgetSpecs(recommendations, module.getSourceId())
						.then(results => {
							const widgetSpecs = [];
							// Cleanup the internal model ref and replace it with an inline model info
							results.forEach(({ spec }) => {
								if(spec.data && spec.data.dataViews){
									spec.data.dataViews.forEach(dataView => {
										dataView.model = {
											assetId : modelInfo.assetId,
											type: modelInfo.type
										};
										delete dataView.modelRef;
									});
								}
								widgetSpecs.push(spec);
							});
							return widgetSpecs;
						});
				});
			});
		}

		_getModule(dataSources, modelInfo) {
			const sourceCollection = dataSources.getSourcesCollection();
			const sourcesId = sourceCollection.getSourceIdsFromAssetId(modelInfo.assetId);
			let modelRef;
			if (sourcesId.length > 0) {
				modelRef = sourcesId[0];
			} else {
				modelRef = sourceCollection.addSource(modelInfo);
			}
			return dataSources.getModule(modelRef);
		}

		renderLiveWidgetPreview(options) {
			return this._getLiveWidgetPreviewClass().then((_LiveWidgetClass) => {
				const preview = new _LiveWidgetClass(options);
				return preview.render().then(() => {
					return preview;
				});
			});
		}

	}
	return LiveWidgetService;
});
